import styled from "styled-components";
import { COLOR_PRIMARY } from "../../constants/color";

type Props = {
  page: number;
  totalPage: number;
  onSetPage: (page: number) => void;
  onPrevious: () => void;
  onNext: () => void;
};

export const Pagination: React.FC<Props> = ({
  page,
  totalPage,
  onSetPage,
  onPrevious,
  onNext,
}) => {
  const _renderPageItem = (
    pageNum: number,
    isCurrentPage: boolean,
    isEmpty?: boolean,
    wide?: boolean,
  ) => {
    if (isEmpty) {
      return <StyledEllipsis key={`pagination-ellipsis-${pageNum}`}>...</StyledEllipsis>;
    }
    return (
      <StyledPageButton
        key={`pagination-${pageNum}`}
        onClick={() => {
          if (!isCurrentPage && pageNum > 0) onSetPage(pageNum);
        }}
        $isCurrentPage={isCurrentPage}
        disabled={isCurrentPage}
        type="button"
      >
        {pageNum}
      </StyledPageButton>
    );
  };

  const _renderMultiplePageItems = () => {
    if (totalPage === 0) {
      return null;
    }
    if (totalPage <= 7) {
      return Array.from({ length: totalPage }, (_, i) =>
        _renderPageItem(i + 1, page === i + 1)
      );
    }
    if (page <= 4) {
      return (
        <>
          {_renderPageItem(1, page === 1)}
          {_renderPageItem(2, page === 2)}
          {_renderPageItem(3, page === 3)}
          {_renderPageItem(4, page === 4)}
          {_renderPageItem(5, page === 5)}
          <StyledEllipsis>...</StyledEllipsis>
          {_renderPageItem(totalPage, page === totalPage)}
        </>
      );
    }
    if (page >= totalPage - 3) {
      return (
        <>
          {_renderPageItem(1, page === 1)}
          <StyledEllipsis>...</StyledEllipsis>
          {_renderPageItem(totalPage - 4, page === totalPage - 4)}
          {_renderPageItem(totalPage - 3, page === totalPage - 3)}
          {_renderPageItem(totalPage - 2, page === totalPage - 2)}
          {_renderPageItem(totalPage - 1, page === totalPage - 1)}
          {_renderPageItem(totalPage, page === totalPage)}
        </>
      );
    }
    return (
      <>
        {_renderPageItem(1, page === 1)}
        <StyledEllipsis>...</StyledEllipsis>
        {_renderPageItem(page - 1, false)}
        {_renderPageItem(page, true)}
        {_renderPageItem(page + 1, false)}
        <StyledEllipsis>...</StyledEllipsis>
        {_renderPageItem(totalPage, page === totalPage)}
      </>
    );
  };

  return (
    <StyledPaginationBar>
      <StyledNavButton
        onClick={onPrevious}
        disabled={page === 1}
        aria-label="Previous page"
      >
        <span aria-hidden>‹</span> Previous
      </StyledNavButton>
      <StyledPageList>{_renderMultiplePageItems()}</StyledPageList>
      <StyledNavButton
        onClick={onNext}
        disabled={page === totalPage}
        aria-label="Next page"
      >
        Next <span aria-hidden>›</span>
      </StyledNavButton>
    </StyledPaginationBar>
  );
};

const StyledPaginationBar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0 0 0;
  margin: 0 0 20px 0;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
    margin: 0 0 16px 0;
    flex-wrap: wrap;
  }

  @media (max-width: 480px) {
    gap: 8px;
    margin: 0 0 12px 0;
  }
`;

const StyledPageList = styled.div`
  display: flex;
  gap: 6px;

  @media (max-width: 768px) {
    gap: 4px;
  }

  @media (max-width: 480px) {
    gap: 3px;
  }
`;

const StyledPageButton = styled.button<{ $isCurrentPage?: boolean }>`
  min-width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;

  background: ${({ $isCurrentPage }) =>
    $isCurrentPage ? COLOR_PRIMARY : "#fff"};
  color: ${({ $isCurrentPage }) =>
    $isCurrentPage ? "#fff" : COLOR_PRIMARY};
  font-weight: 700;
  font-size: 14px;
  cursor: ${({ $isCurrentPage }) =>
    $isCurrentPage ? "default" : "pointer"};
  box-shadow: ${({ $isCurrentPage }) =>
    $isCurrentPage ? "0 2px 8px rgba(44, 62, 80, 0.08)" : "none"};
  border: 1.5px solid
    ${({ $isCurrentPage }) =>
    $isCurrentPage ? COLOR_PRIMARY : "#dbe2ea"};
  transition: background 0.2s, color 0.2s;
  &:hover:not(:disabled):not([aria-current]) {
    background: ${({ $isCurrentPage }) =>
    $isCurrentPage ? COLOR_PRIMARY : "#eaf1fb"};
  }

  @media (max-width: 768px) {
    min-width: 28px;
    height: 28px;
    font-size: 12px;
    border-radius: 4px;
  }

  @media (max-width: 480px) {
    min-width: 24px;
    height: 24px;
    font-size: 11px;
    border-radius: 3px;
  }
`;

const StyledNavButton = styled.button`
  min-width: 80px;
  height: 32px;
  border-radius: 6px;
  border: 1.5px solid #dbe2ea;
  background: #fff;
  color: ${COLOR_PRIMARY};
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  transition: background 0.2s, color 0.2s;
  &:disabled {
    color: #b3b3b3;
    border-color: #f0f0f0;
    cursor: not-allowed;
    background: #f6f8fa;
  }

  @media (max-width: 768px) {
    min-width: 70px;
    height: 28px;
    font-size: 12px;
    border-radius: 4px;
  }

  @media (max-width: 480px) {
    min-width: 60px;
    height: 24px;
    font-size: 11px;
    border-radius: 3px;
  }
`;

const StyledEllipsis = styled.span`
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b3b3b3;
  font-size: 18px;
  font-weight: 700;

  @media (max-width: 768px) {
    min-width: 28px;
    height: 28px;
    font-size: 16px;
  }

  @media (max-width: 480px) {
    min-width: 24px;
    height: 24px;
    font-size: 14px;
  }
`;
